import React, { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";

// Generate CSRF token
export const generateCSRFToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
};

// Base64 decoding function for encoded query parameters
export const decryptData = (base64String) => {
  try {
    const decoded = atob(base64String);
    return JSON.parse(decoded);
  } catch (err) {
    console.error("Base64 decoding failed", err);
    return null;
  }
};

// Cryptographically secure UUID generator
// Uses crypto.randomUUID() or crypto.getRandomValues() for security-sensitive session IDs
export const generateUUID = () => {
  // Ensure crypto is available (supported in all modern browsers)
  if (typeof crypto === "undefined") {
    throw new Error(
      "Crypto API is not available. This application requires a modern browser with crypto support."
    );
  }

  // Try crypto.randomUUID() first (modern browsers/Node.js 15.6+)
  if (crypto.randomUUID && typeof crypto.randomUUID === "function") {
    try {
      return crypto.randomUUID();
    } catch (e) {
      // randomUUID not available or failed, fall through to getRandomValues
      // This is intentional - we want to try the next method
      console.debug(
        "crypto.randomUUID failed, using getRandomValues fallback:",
        e.message
      );
    }
  }

  // Use crypto.getRandomValues() (widely supported, cryptographically secure)
  if (crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    // Set version (4) and variant (10) bits for UUID v4
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 10

    const hex = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20, 32),
    ].join("-");
  }

  // If we reach here, crypto.getRandomValues is not available either
  throw new Error(
    "Cryptographically secure random number generation is not available. Please use a modern browser."
  );
};

const MerchantChatbot = () => {
  const [messages, setMessages] = useState([
    {
      text: "Welcome to the Merchant AI Assistant which provides guidance on how to manage your store. AI-generated responses may be inaccurate - please refer to the <a href='https://selleruniversity.ishopchangi.com' target='_blank' rel='noopener noreferrer'>Seller University</a> or contact the Merchant Service Desk via <a href='https://cag.okta.com' target='_blank' rel='noopener noreferrer'>Okta SSO</a> for definitive guidance.",
      sender: "bot",
      id: "init",
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionData, setSessionData] = useState({
    sessionId: "",
    email: "",
    storeId: "",
    storeName: "",
  });
  const [suggestions, setSuggestions] = useState([]);
  const [csrfToken] = useState(generateCSRFToken());

  const messagesEndRef = useRef(null);
  const API_URL = "https://chatbot-api.ishopchangi.com/cbs/postLexBot";

  // Initialize Session and URL Params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const guid = generateUUID();

    // Check if there's an encrypted query parameter (the first parameter without a name)
    const encryptedParam = window.location.search.substring(1); // Remove the '?'

    let email = "";
    let storeId = "";
    let storeName = "";

    // Check if this looks like a base64 encoded parameter (no key= prefix)
    // Base64 strings can contain = at the end for padding, so we check if there's a key before the =
    const hasKey =
      encryptedParam.includes("=") &&
      !encryptedParam.startsWith("=") &&
      encryptedParam.indexOf("=") > 0 &&
      encryptedParam.indexOf("=") < encryptedParam.length - 2;

    if (encryptedParam && !hasKey) {
      // This is an encrypted parameter
      const decryptedData = decryptData(encryptedParam);
      if (decryptedData) {
        email = decryptedData.email || "";
        // Convert storeId to string if it's a number
        storeId = decryptedData.storeId ? String(decryptedData.storeId) : "";
        storeName = decryptedData.storeName || "";
        console.log("Decrypted data:", { email, storeId, storeName });
      } else {
        console.warn("Failed to decrypt query parameter");
      }
    } else {
      // Fall back to plain query parameters for backward compatibility
      email = urlParams.get("email") || "";
      storeId = urlParams.get("storeId") || "";
      storeName = urlParams.get("storeName") || "";
    }

    setSessionData({ sessionId: guid, email, storeId, storeName });
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (
      messagesEndRef.current &&
      typeof messagesEndRef.current.scrollIntoView === "function"
    ) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping, suggestions]);

  const handleSend = async (textOverride = null) => {
    const messageText = textOverride || input.trim();
    if (!messageText) return;

    // Add User Message
    const userMsg = { text: messageText, sender: "user", id: Date.now() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSuggestions([]);
    setIsTyping(true);

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "x-api-key": "UbP26iZ1MZ6DeIwjLjxnV6GzWI2922t52ByyeRFy",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          message: messageText,
          sessionId: sessionData.sessionId,
          store: sessionData.storeName || "-",
          storeId:
            sessionData.storeId &&
            typeof sessionData.storeId === "string" &&
            sessionData.storeId.trim()
              ? sessionData.storeId
              : sessionData.storeId && typeof sessionData.storeId === "number"
              ? String(sessionData.storeId)
              : "0",
          email: sessionData.email,
          csrfToken: csrfToken,
        }),
      });

      const result = await response.json();

      if (result.success) {
        processBotResponse(result.data);
      } else {
        throw new Error("API returned success: false");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        {
          text: "Sorry, I encountered an error. Please try again.",
          sender: "bot",
          isError: true,
          id: Date.now() + 1,
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const processBotResponse = (data) => {
    const fallbackPhrases = [
      "I'm still learning",
      "Submit a ticket",
      "Merchant Helpdesk",
    ];
    const isGenericFallback = fallbackPhrases.some((phrase) =>
      data.text?.includes(phrase)
    );

    // Clean sources
    let cleanSources = [];
    if (!isGenericFallback && data.sources) {
      data.sources.forEach((source) => {
        if (typeof source === "string") {
          cleanSources.push(
            ...source
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
          );
        }
      });
    }

    setMessages((prev) => [
      ...prev,
      {
        text: data.text,
        sender: "bot",
        sources: cleanSources,
        id: Date.now(),
      },
    ]);

    if (data.quickReplies) {
      setSuggestions(data.quickReplies);
    }
  };

  const formatMessage = (text) => {
    // Sanitize HTML to prevent XSS attacks
    const sanitizeHTML = (dirty) => {
      return DOMPurify.sanitize(dirty, {
        ALLOWED_TAGS: ["a", "br", "strong", "p", "span"],
        ALLOWED_ATTR: ["href", "target", "rel"],
      });
    };

    return text.split("\n").map((line, i) => {
      // Bullet points
      if (line.startsWith("- ")) {
        const sanitizedContent = sanitizeHTML(line.substring(2));
        return (
          <span key={i}>
            <br />
            <strong>•</strong>{" "}
            <span dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
          </span>
        );
      }
      // Numbered lists
      if (/^\d+\.\s/.test(line)) {
        const sanitizedContent = sanitizeHTML(line.replace(/^\d+\.\s/, ""));
        return (
          <span key={i}>
            <br />
            <strong>{line.match(/^\d+\.\s/)[0]}</strong>
            <span dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
          </span>
        );
      }
      const sanitizedContent = sanitizeHTML(line);
      return (
        <p
          key={i}
          style={{ margin: "0 0 8px 0" }}
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
      );
    });
  };

  return (
    <div className="chat-wrapper">
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .chat-wrapper { font-family: 'Segoe UI', sans-serif; width: 100%; height: 100vh; display: flex; flex-direction: column; overflow: hidden; }
        .chat-container { width: 100%; height: 100vh; max-height: 100vh; background: white; display: flex; flex-direction: column; overflow: hidden; }
        .chat-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; text-align: center; font-weight: bold; }
        .chat-messages { flex: 1; padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 15px; }
        .message { max-width: 80%; padding: 12px 16px; border-radius: 18px; line-height: 1.4; font-size: 14px; }
        .user-message { background: #007bff; color: white; align-self: flex-end; }
        .bot-message { background: #f1f3f4; color: #333; align-self: flex-start; }
        .error-message { background: #ffebee; color: #c62828; border-left: 4px solid #f44336; }
        .sources-container { margin-top: 10px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 12px; }
        .source-link { display: block; color: #1976d2; text-decoration: none; margin: 4px 0; /* word-break: break-all; */ }
        .suggestions-container { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
        .suggestion-button { background: #e3f2fd; border: 2px solid #2196f3; color: #1976d2; padding: 8px 12px; border-radius: 20px; cursor: pointer; text-align: center; transition: 0.2s; font-size: 13px; }
        .suggestion-button:hover { background: #2196f3; color: white; }
        .chat-input-container { padding: 15px; background: #f8f9fa; border-top: 1px solid #e0e0e0; display: flex; gap: 10px; }
        .chat-input { flex: 1; padding: 10px 15px; border: 2px solid #e0e0e0; border-radius: 25px; outline: none; }
        .send-button { background: #007bff; color: white; border: none; border-radius: 50%; width: 40px; height: 40px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .send-button:disabled { background: #ccc; }
        .typing-indicator { padding: 12px; background: #f1f3f4; border-radius: 18px; align-self: flex-start; margin-bottom: 10px; }
        .dots { display: flex; gap: 4px; }
        .dot { width: 8px; height: 8px; background: #999; border-radius: 50%; animation: bounce 1.5s infinite; }
        @keyframes bounce { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-6px); } }
        .session-info { display: none; font-size: 11px; color: #666; text-align: center; padding: 8px; background: #f8f9fa; border-top: 1px solid #eee; }
        .break-after-40 {
          /* Use a monospace font for exact precision */
         
          
          /* Limits the width to 45 characters */
          max-width: 40ch;
          
          /* Ensures words break if a single word is longer than 40 chars */
          overflow-wrap: break-word;
          /* word-break: break-all; */
        }
      `}</style>

      <div className="chat-container">
        <div className="chat-header">Merchant Chatbot</div>

        <div className="chat-messages">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`message ${msg.sender}-message ${
                msg.isError ? "error-message" : ""
              } ${msg.sender === "bot" ? "break-after-40" : ""}`}
            >
              {formatMessage(msg.text)}

              {msg.sources && msg.sources.length > 0 && (
                <div className="sources-container">
                  <strong>Sources:</strong>
                  {msg.sources.map((src, i) => (
                    <a
                      key={i}
                      href={src}
                      target="_blank"
                      rel="noreferrer"
                      className="source-link"
                    >
                      {src}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}

          {isTyping && (
            <div className="typing-indicator">
              <div className="dots">
                <div className="dot" />
                <div className="dot" style={{ animationDelay: "0.2s" }} />
                <div className="dot" style={{ animationDelay: "0.4s" }} />
              </div>
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="suggestions-container">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  className="suggestion-button"
                  onClick={() => handleSend(s.title)}
                >
                  {s.title}
                </button>
              ))}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-container">
          <input
            className="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSend()}
            placeholder="Type your message..."
          />
          <button
            className="send-button"
            onClick={() => handleSend()}
            disabled={!input.trim()}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>

        <div className="session-info">
          Session ID: {sessionData.sessionId}
          {sessionData.storeId && ` | Store: ${sessionData.storeId}`}
          {sessionData.storeName && ` | ${sessionData.storeName}`}
        </div>
      </div>
    </div>
  );
};

export default MerchantChatbot;
